/**
 * Configuration fetcher - retrieves Analyzify headless config from Storefront API
 *
 * Returns raw metafield data without transformation.
 * Use HeadlessConfig accessor class to read typed values.
 */

import type { HeadlessMetafield } from '../types/headless-config';
import { STOREFRONT_API_VERSION } from './constants';

/**
 * GraphQL query to fetch Analyzify configuration from metafield
 */
const CONFIG_QUERY = `
  query AnalyzifyConfig {
    shop {
      metafield(namespace: "analyzify_app_metafield_export", key: "headless_config") {
        value
      }
    }
  }
`;

/**
 * Fetch Analyzify headless configuration from Shopify Storefront API
 *
 * Returns the raw metafield object without any transformation.
 * The returned object has keys like:
 *   "analyzify.ga4_status": { type: "boolean", value: "true" }
 *   "analyzify.ga4_id": { type: "single_line_text_field", value: "G-XXXXXXX" }
 *
 * @param shopDomain - The shop domain (e.g., 'your-store.myshopify.com')
 * @param storefrontToken - The Storefront API access token
 * @returns Raw HeadlessMetafield object
 */
export async function fetchHeadlessConfig(
  shopDomain: string,
  storefrontToken: string
): Promise<HeadlessMetafield> {
  // Normalize domain
  const domain = shopDomain.replace(/^https?:\/\//, '').replace(/\/$/, '');
  const url = `https://${domain}/api/${STOREFRONT_API_VERSION}/graphql.json`;

  const response = await fetch(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'X-Shopify-Storefront-Access-Token': storefrontToken,
    },
    body: JSON.stringify({
      query: CONFIG_QUERY,
    }),
  });

  if (!response.ok) {
    throw new Error(`Failed to fetch Analyzify config: ${response.status} ${response.statusText}`);
  }

  const data = await response.json();

  if (data.errors) {
    throw new Error(`GraphQL errors: ${JSON.stringify(data.errors)}`);
  }

  const metafieldValue = data?.data?.shop?.metafield?.value;

  if (!metafieldValue) {
    throw new Error(
      'Analyzify configuration not found. Please ensure the headless config is enabled in Analyzify app settings.'
    );
  }

  try {
    // Return raw metafield without transformation
    const parsed = JSON.parse(metafieldValue);
    return parsed as HeadlessMetafield;
  } catch (e) {
    throw new Error(`Failed to parse Analyzify config: ${e}`);
  }
}
